import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type React from "react";
import { useIsMobileView } from "../../hooks/ui-hook";
import AppointmentSelectionModal from "./AppointmentSelectionModal";
import MFACodeModal from "./MFACodeModal";
import { useTransfer } from "./TransferContext";

interface TransferToEHRButtonProps {
  className?: string;
  icon?: React.ReactNode;
}

const ACTIVE_STATUSES = ["pending", "logging_in", "awaiting_mfa", "patient_lookup", "template_mapping", "writing_note"];

const TransferToEHRButton: React.FC<TransferToEHRButtonProps> = ({ className = "", icon: defaultIcon }) => {
  const isMobile = useIsMobileView();
  const {
    isLoading,
    credentialStatus,
    currentStatus,
    appointmentOptions,
    showAppointmentModal,
    isSelectingAppointment,
    isNoteTypeSelection,
    showMFAModal,
    isSubmittingMFA,
    startTransfer,
    selectAppointment,
    cancelAppointment,
    submitMFACode,
    cancelMFA,
  } = useTransfer();

  if (isMobile) return null;

  // Compute button state
  const isInProgress = ACTIVE_STATUSES.includes(currentStatus);
  const isAwaitingAppointment = currentStatus === "awaiting_appointment";
  const isAwaitingMFA = currentStatus === "awaiting_mfa";
  const isCompleted = currentStatus === "completed";
  const isFailed = currentStatus === "failed";
  const hasCredentials = credentialStatus?.has_credential ?? false;
  const isDisabled = isLoading || isInProgress || isCompleted || isAwaitingAppointment || isAwaitingMFA;

  // Compute button text and icon
  let buttonText = "Transfer to EHR";
  let icon = null;

  // Determine if we're in a "processing" visual state that should maintain primary styling
  const isProcessingState = isLoading || isInProgress || isAwaitingAppointment || isAwaitingMFA;
  const style = { color: "white" };
  if (isLoading) {
    buttonText = "Checking...";
    icon = <LoadingOutlined style={style} />;
  } else if (!hasCredentials) {
    buttonText = "Transfer to EHR";
  } else if (isInProgress) {
    buttonText = "Transferring...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingMFA) {
    buttonText = "Enter MFA Code...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingAppointment) {
    buttonText = isNoteTypeSelection ? "Select Note Template..." : "Select Appointment...";
    icon = <LoadingOutlined style={style} />;
  } else if (isCompleted) {
    buttonText = "Transferred";
    icon = <CheckOutlined style={style} />;
  } else if (isFailed) {
    buttonText = "Retry Transfer";
  } else if (hasCredentials) {
    buttonText = "Transfer to EHR";
  }

  return (
    <>
      <Button
        type="primary"
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          ...((isProcessingState || isCompleted) && {
            backgroundColor: "#1677ff",
            borderColor: "#1677ff",
            color: "white",
            opacity: 1,
          }),
        }}
        onClick={startTransfer}
        disabled={isDisabled}
        icon={icon ?? defaultIcon}
      >
        {buttonText}
      </Button>

      <AppointmentSelectionModal
        visible={showAppointmentModal}
        appointments={appointmentOptions}
        onSelect={selectAppointment}
        onCancel={cancelAppointment}
        loading={isSelectingAppointment}
        title={isNoteTypeSelection ? "Select Note Template" : undefined}
        description={
          isNoteTypeSelection
            ? "Multiple note templates are available. Please select the template to use for this note:"
            : undefined
        }
      />

      <MFACodeModal visible={showMFAModal} onSubmit={submitMFACode} onCancel={cancelMFA} loading={isSubmittingMFA} />
    </>
  );
};

export default TransferToEHRButton;
